import cloneDeep from "lodash.clonedeep";
import maxBy from "lodash.maxby";
import { v4 } from "uuid";

import {
  RedirectDto,
  RedirectFilter,
  ShopifyTemplateTypeType,
  UpdateDestinationUrlPayloadAction,
  UpdateOriginUrlPayloadAction,
  UrlRedirectType,
  VariationDto,
  VariationFe,
} from "@intelligems/ig-types";
import * as rtk from "@reduxjs/toolkit";
import { createSelector, type PayloadAction } from "@reduxjs/toolkit";
import {
  getSpecificRedirectsUtil,
  getSpecificVariationRedirectsUtil,
} from "@src/features/experiments/components/redirects/getSpecificRedirects";
import type { RootState } from "@src/store/rootState";
import { initialRedirectState } from "../../api/initials";
import {
  ensureAllTemplateType,
  ensureRedirectGroupOrder,
  ensureTestGroupRedirects,
  syncAllTemplateSuffixes,
  syncPersonalizationTemplateSuffixes,
  syncTemplateSuffixes,
} from "../../api/reducers/redirects/redirectReducerActions";
import { IRedirectsSliceState } from "../types/redirects";

const sliceInitialRedirectsState: IRedirectsSliceState = {
  isSuccess: false,
  redirects: [] as RedirectDto[],
  storedRedirects: [] as RedirectDto[],
  redirectErrors: [] as string[],
};

const redirectsSlice = rtk.createSlice({
  name: "redirects",
  initialState: sliceInitialRedirectsState,
  reducers: {
    resetRedirects: (draft) => {
      draft.isSuccess = false;
      draft.redirects = [];
      draft.storedRedirects = [];
      draft.redirectErrors = [];
    },
    setRedirectsByVariationId: (
      draft,
      action: PayloadAction<{ redirects: RedirectDto[]; variationId: string }>
    ) => {
      const updatedDraft = draft.redirects.filter(
        (prod) => prod.variationId !== action.payload.variationId
      );

      const dupRedirects: RedirectDto[] = action.payload.redirects.map(
        (redirect) => {
          return {
            ...redirect,
            id: v4(),
            variationId: action.payload.variationId,
          };
        }
      );

      draft.redirects = [...updatedDraft, ...dupRedirects];
    },
    resetRedirectsByVariationId: (
      draft,
      action: PayloadAction<{ variationId: string }>
    ) => {
      draft.redirects = draft.redirects.filter(
        (prod) => prod.variationId !== action.payload.variationId
      );
    },
    initRedirectsState: (
      draft,
      action: PayloadAction<{ redirects: RedirectDto[]; domain: string }>
    ) => {
      draft.isSuccess = true;
      draft.redirects = action.payload.redirects;
      draft.storedRedirects = action.payload.redirects;
      draft.redirectErrors = [];
    },
    initRedirectsFromVariationsState: (
      draft,
      action: PayloadAction<{ variations: VariationDto[] }>
    ) => {
      const { variations } = action.payload;
      const tempRedirects: RedirectDto[] = [];

      variations.forEach((variation) => {
        if (variation.redirects) {
          tempRedirects.push(...variation.redirects);
        }
      });

      if (tempRedirects.length) {
        draft.isSuccess = true;
        draft.redirects = tempRedirects;
        draft.storedRedirects = tempRedirects;
      }
    },
    initUrlRedirectsState: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        isPersonalization?: boolean;
        /**
         * Explicit `skip` for the rows created here. Pass `false` when the
         * redirect belongs to one group that the user is configuring, so that
         * it fires even when that group is the control. Omit it in a test-wide
         * split URL test, where the rows form one origin page's matrix row and
         * control's row exists to mean "stay on this page" — there
         * `initialRedirectState` falls back to `variation.isControl`.
         */
        skip?: boolean;
      }>
    ) => {
      draft.isSuccess = true;
      draft.redirectErrors = [];

      action.payload.variations.forEach((variation: VariationFe) => {
        draft.redirects.push(
          initialRedirectState({
            variation,
            group: 1,
            isThemeTest: false,
            isTemplateTest: false,
            skip:
              action.payload.skip ??
              (action.payload.isPersonalization ? false : undefined),
          })
        );
      });
      draft.storedRedirects = draft.redirects;
    },
    checkRedirects: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        isPersonalization: boolean;
      }>
    ) => {
      const redirects = ensureTestGroupRedirects(
        draft.redirects,
        action.payload.variations
      );
      if (action.payload.isPersonalization) {
        draft.redirects = redirects;
        return;
      }
      // Keeps each template group enrolling from exactly its variations'
      // current templates: drops those of removed variations and any stale
      // ones already in the loaded state (e.g. inherited by a duplicate).
      const synced = syncAllTemplateSuffixes(redirects);
      draft.redirects = synced.some((redirect, i) => redirect !== redirects[i])
        ? synced
        : redirects;
    },
    addNewRedirectOrigin: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        filter: RedirectFilter;
        isPersonalization?: boolean;
      }>
    ) => {
      // `group` numbers a redirect within the scope it is created for. A
      // test-wide split URL test passes every variation, so the scope is the
      // whole test and this is the global next group. A per-variation mods
      // panel passes only that group's variation, so the number continues that
      // variation's own sequence and the other groups are left untouched.
      const scopedVariationIds = new Set(
        action.payload.variations.map((variation) => variation.id)
      );
      const scopedRedirects = draft.redirects.filter((redirect) =>
        scopedVariationIds.has(redirect.variationId)
      );
      const group = (maxBy(scopedRedirects, (_) => _.group)?.group || 0) + 1;
      action.payload.variations.forEach((variation: VariationFe) => {
        draft.redirects.push(
          initialRedirectState({
            variation,
            group,
            isThemeTest: false,
            redirectFilter: action.payload.filter,
            skip: action.payload.isPersonalization ? false : undefined,
          })
        );
      });
    },
    updateAllRedirectFilters: (
      draft,
      action: PayloadAction<{
        filter: RedirectFilter;
      }>
    ) => {
      draft.redirects.forEach((redirect) => {
        redirect.filter = action.payload.filter;
      });
    },
    updateAllUrlRedirectType: (
      draft,
      action: PayloadAction<{
        redirectType: UrlRedirectType;
      }>
    ) => {
      draft.redirects.forEach((redirect) => {
        redirect.redirectType = action.payload.redirectType;
      });
    },
    deleteAllUrlRedirects: (draft) => {
      draft.redirects = draft.redirects.filter(
        (item) => item.isThemeTest || item.isTemplateTest
      );
    },
    updateOriginUrl: (
      draft,
      action: PayloadAction<UpdateOriginUrlPayloadAction>
    ) => {
      // offer test redirects
      if (action.payload.redirectId) {
        const index = draft.redirects.findIndex(
          (redirect) => redirect.id === action.payload.redirectId
        );
        if (index !== -1) {
          draft.redirects[index].originUrl = action.payload.value;
        }
      } else {
        for (const redirect of draft.redirects) {
          if (redirect.group === action.payload.group) {
            redirect.originUrl = action.payload.value;
            if (action.payload.queryParams) {
              action.payload.queryParams.forEach((queryParam) => {
                redirect.queryParams.push({
                  key: queryParam.key,
                  value: queryParam.value,
                });
              });
            }
          }
        }
      }
    },
    updateDestinationUrl: (
      draft,
      action: PayloadAction<{
        redirectId: string;
        value: string;
        queryParams?: { key: string; value: string }[];
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].destinationUrl = action.payload.value;
        if (action.payload.queryParams) {
          action.payload.queryParams.forEach((queryParam) => {
            draft.redirects[index].queryParams.push({
              key: queryParam.key,
              value: queryParam.value,
            });
          });
        }
      }
    },
    updateDestinationUrlByVariationId: (
      draft,
      action: PayloadAction<UpdateDestinationUrlPayloadAction>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.variationId === action.payload.variationId
      );
      if (index !== -1) {
        draft.redirects[index].destinationUrl = action.payload.value;
        if (action.payload.queryParams) {
          action.payload.queryParams.forEach((queryParam) => {
            draft.redirects[index].queryParams.push({
              key: queryParam.key,
              value: queryParam.value,
            });
          });
        }
      }
    },
    deleteRedirectOrigin: (
      draft,
      action: PayloadAction<{
        group: number;
        /**
         * Set from a per-variation mods panel, where a group is that group's
         * own redirect. Omitted in a test-wide split URL test, where a group is
         * one origin page and deleting it must remove that page's row for every
         * variation.
         */
        variationId?: string;
      }>
    ) => {
      const { group, variationId } = action.payload;
      draft.redirects = ensureRedirectGroupOrder(
        draft.redirects.filter((item) =>
          variationId
            ? !(item.group === group && item.variationId === variationId)
            : item.group !== group
        ),
        variationId
      );
    },
    updateRedirectGroupFilter: (
      draft,
      action: PayloadAction<{ group: number; filter: RedirectFilter }>
    ) => {
      for (const redirect of draft.redirects) {
        if (redirect.group === action.payload.group) {
          redirect.filter = action.payload.filter;
        }
      }
    },
    updateRedirectType: (
      draft,
      action: PayloadAction<{ group: number; redirectType: UrlRedirectType }>
    ) => {
      for (const redirect of draft.redirects) {
        if (redirect.group === action.payload.group) {
          redirect.redirectType = action.payload.redirectType;
          if (action.payload.redirectType === "fullReplacement") {
            redirect.find = undefined;
          }
        }
      }
    },
    updateRedirectFind: (
      draft,
      action: PayloadAction<{ redirectId: string; find: string }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].find = action.payload.find;
      }
    },
    updateQueryParam: (
      draft,
      action: PayloadAction<{
        redirectId: string;
        queryParamIndex: number;
        key: string;
        value: string;
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].queryParams[action.payload.queryParamIndex] = {
          key: action.payload.key,
          value: action.payload.value,
        };
      }
    },
    deleteQueryParam: (
      draft,
      action: PayloadAction<{
        redirectId: string;
        queryParamIndex: number;
        isControl?: boolean;
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].queryParams.splice(
          action.payload.queryParamIndex,
          1
        );
        // for template redirects
        if (action.payload.isControl) {
          draft.redirects[index].templateType = null;
        }
      }
    },
    addQueryParam: (
      draft,
      action: PayloadAction<{
        redirectId: string;
      }>
    ) => {
      const index = draft.redirects.findIndex(
        (redirect) => redirect.id === action.payload.redirectId
      );
      if (index !== -1) {
        draft.redirects[index].queryParams.push({
          key: "",
          value: "",
        });
      }
    },
    updateRedirectOnce: (
      draft,
      action: PayloadAction<{
        group: number;
        value: boolean;
      }>
    ) => {
      draft.redirects.forEach((redirect) => {
        if (redirect.group === action.payload.group) {
          redirect.redirectOnce = action.payload.value;
        }
      });
    },
    initializeThemeRedirect: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        isPersonalization?: boolean;
      }>
    ) => {
      const group = (maxBy(draft.redirects, (_) => _.group)?.group || 0) + 1;
      action.payload.variations.forEach((variation: VariationFe) => {
        draft.redirects.push(
          initialRedirectState({
            variation,
            group,
            isThemeTest: true,
            skip: action.payload.isPersonalization ? false : undefined,
          })
        );
      });
    },
    updateTemplateRedirect: (
      draft,
      action: PayloadAction<{
        redirectState: RedirectDto;
        isControl: boolean;
        isPersonalization: boolean;
        isUserTriggered?: boolean;
      }>
    ) => {
      let draftCopy: RedirectDto[] = cloneDeep(draft.redirects);

      const index = draftCopy.findIndex((red) => {
        return (
          red.variationId === action.payload.redirectState.variationId &&
          red.group === action.payload.redirectState.group &&
          red.isTemplateTest
        );
      });

      if (index !== -1) {
        draftCopy[index] = action.payload.isPersonalization
          ? syncPersonalizationTemplateSuffixes(action.payload.redirectState)
          : action.payload.redirectState;

        if (action.payload.isControl) {
          draftCopy = ensureAllTemplateType(
            draftCopy,
            action.payload.redirectState.templateType!,
            action.payload.redirectState.group
          );
        }

        if (!action.payload.isPersonalization) {
          draftCopy = syncTemplateSuffixes(
            draftCopy,
            action.payload.redirectState.group
          );
        }
      }
      draft.redirects = draftCopy;
      draftCopy.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
      if (action.payload.isUserTriggered === false) {
        draft.storedRedirects = draft.redirects;
      }
    },
    removeTemplateRedirectGroup: (
      draft,
      action: PayloadAction<{ group: number; variationId?: string }>
    ) => {
      const { group, variationId } = action.payload;

      draft.redirects = draft.redirects.filter((redirect) => {
        if (variationId) {
          return (
            redirect.group !== group || redirect.variationId !== variationId
          );
        }
        return redirect.group !== group;
      });
      draft.redirects.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
    },
    updatePersonalizationTemplateRedirect: (
      draft,
      action: PayloadAction<{
        redirectState: RedirectDto;
        index: number;
        tabValue?: number;
      }>
    ) => {
      let draftCopy: RedirectDto[] = cloneDeep(draft.redirects);
      const redirectIndex = draftCopy.findIndex(
        (redirect) =>
          redirect.group === action.payload.redirectState.group &&
          redirect.variationId === action.payload.redirectState.variationId &&
          redirect.isTemplateTest
      );

      if (redirectIndex !== -1) {
        const currentRedirect = draftCopy[redirectIndex];

        if (action.payload.index === 0) {
          currentRedirect.templateSuffixes =
            action.payload.redirectState.templateSuffixes ?? [];

          if (
            currentRedirect.templateType &&
            currentRedirect.queryParams.length &&
            !currentRedirect.queryParams[0]?.value.includes(
              currentRedirect.templateType
            )
          ) {
            currentRedirect.queryParams = [];
          }
        } else if (action.payload.index === 1) {
          currentRedirect.queryParams =
            action.payload.redirectState.queryParams ?? [];
        }

        currentRedirect.templateType =
          action.payload.redirectState.templateType;

        draftCopy[redirectIndex] =
          syncPersonalizationTemplateSuffixes(currentRedirect);
      } else {
        draftCopy.push(action.payload.redirectState);
      }

      draft.redirects = draftCopy;
      draftCopy.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
    },
    setAiTemplateRedirects: (
      draft,
      action: PayloadAction<{
        redirects: RedirectDto[];
        domain: string;
      }>
    ) => {
      // First initialize the redirects state with AI-generated redirects
      draft.isSuccess = true;
      draft.redirects = action.payload.redirects;
      draft.storedRedirects = action.payload.redirects;
      draft.redirectErrors = [];

      // Then process each redirect with template-specific logic
      let draftCopy = cloneDeep(draft.redirects);

      // Find control variation redirects to ensure consistency
      // Need to identify control variation from the redirects themselves
      const groupedRedirects = draftCopy.reduce(
        (acc, redirect) => {
          if (!acc[redirect.group]) acc[redirect.group] = [];
          acc[redirect.group].push(redirect);
          return acc;
        },
        {} as Record<number, typeof draftCopy>
      );

      // For each group, if any redirect has a templateType, ensure consistency
      Object.entries(groupedRedirects).forEach(([groupStr, groupRedirects]) => {
        const group = Number(groupStr);
        const redirectWithTemplateType = groupRedirects.find(
          (r) => r.templateType
        );

        if (redirectWithTemplateType && redirectWithTemplateType.templateType) {
          draftCopy = ensureAllTemplateType(
            draftCopy,
            redirectWithTemplateType.templateType,
            group
          );
        }
      });

      // Ensure all template redirects have proper suffixes
      draftCopy = syncAllTemplateSuffixes(draftCopy);

      // Update final state
      draft.redirects = draftCopy;
      draft.redirects.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
      draft.storedRedirects = draft.redirects;
    },
    setAiThemeRedirects: (
      draft,
      action: PayloadAction<{
        redirects: RedirectDto[];
        domain: string;
      }>
    ) => {
      draft.isSuccess = true;
      const sortedRedirects = [...action.payload.redirects].sort(
        (a, b) => (a.group ?? 0) - (b.group ?? 0)
      );
      draft.redirects = sortedRedirects;
      draft.storedRedirects = sortedRedirects;
      draft.redirectErrors = [];
    },
    updateThemeRedirect: (
      draft,
      action: PayloadAction<{
        redirectState: RedirectDto;
      }>
    ) => {
      const draftCopy: RedirectDto[] = cloneDeep(draft.redirects);

      const index = draftCopy.findIndex(
        (red) =>
          red.variationId === action.payload.redirectState.variationId &&
          red.isThemeTest
      );

      if (index !== -1) {
        draftCopy[index] = action.payload.redirectState;
      }
      draft.redirects = draftCopy;
    },
    deleteAllTemplateOrigins: (draft) => {
      draft.redirects = draft.redirects.filter((item) => !item.isTemplateTest);
    },
    deleteThemeRedirects: (draft) => {
      draft.redirects = draft.redirects.filter((item) => !item.isThemeTest);
    },
    initializeTemplateRedirects: (
      draft,
      action: PayloadAction<{
        variations: VariationFe[];
        templateType: ShopifyTemplateTypeType | null;
        isPersonalization?: boolean;
        group?: number;
        templateSuffixes?: string[];
        isUserTriggered?: boolean;
      }>
    ) => {
      action.payload.variations.forEach((variation: VariationFe) => {
        draft.redirects.push(
          initialRedirectState({
            variation,
            group: action.payload.group ?? 1,
            isThemeTest: false,
            templateType: action.payload.templateType ?? null,
            templateSuffixes: action.payload.templateSuffixes ?? [],
            isTemplateTest: true,
            skip: action.payload.isPersonalization ? false : undefined,
          })
        );
      });
      draft.redirects.sort((a, b) => (a.group ?? 0) - (b.group ?? 0));
      if (action.payload.isUserTriggered === false) {
        draft.storedRedirects = draft.redirects;
      }
    },
    setRedirectErrors: (
      draft,
      action: PayloadAction<{
        error: string | null;
        redirectId: string;
        urlKey: string;
      }>
    ) => {
      if (action.payload.error) {
        if (!draft.redirectErrors.includes(action.payload.error)) {
          draft.redirectErrors.push(action.payload.error);
        }
      } else {
        const index = draft.redirectErrors.findIndex(
          (err) =>
            err === `${action.payload.urlKey}-${action.payload.redirectId}`
        );
        if (index !== -1) {
          draft.redirectErrors.splice(index, 1);
        }
      }
    },
    removeDeletedRedirectErrors: (draft) => {
      const currentRedirectErrors: string[] = [];
      const allRedirectIds = draft.redirects.map((redirect) => redirect.id);

      draft.redirectErrors.forEach((error) => {
        const formattedError = error.replace(
          /^(originUrl-|destinationUrl-|queryParameter-)/,
          ""
        );

        if (allRedirectIds.includes(formattedError)) {
          currentRedirectErrors.push(error);
        }
      });
      draft.redirectErrors = currentRedirectErrors;
    },
    resetRedirectErrors: (draft) => {
      draft.redirectErrors = [];
    },
  },
  selectors: {
    selectedRedirectsState: (state) => state as IRedirectsSliceState,
    selectedRedirects: (state) => state.redirects,
    selectedStoredRedirects: (state) => state.storedRedirects,
    selectedRedirectErrors: (state) => state.redirectErrors,
  },
});

export const {
  resetRedirects,
  initRedirectsState,
  initUrlRedirectsState,
  checkRedirects,
  addNewRedirectOrigin,
  updateAllRedirectFilters,
  deleteAllUrlRedirects,
  updateOriginUrl,
  updateDestinationUrl,
  updateDestinationUrlByVariationId,
  deleteRedirectOrigin,
  updateRedirectGroupFilter,
  updateRedirectType,
  updateRedirectFind,
  updateQueryParam,
  deleteQueryParam,
  addQueryParam,
  updateRedirectOnce,
  initializeThemeRedirect,
  deleteAllTemplateOrigins,
  deleteThemeRedirects,
  updateTemplateRedirect,
  updateThemeRedirect,
  initializeTemplateRedirects,
  removeTemplateRedirectGroup,
  setRedirectErrors,
  removeDeletedRedirectErrors,
  resetRedirectErrors,
  updateAllUrlRedirectType,
  setRedirectsByVariationId,
  resetRedirectsByVariationId,
  initRedirectsFromVariationsState,
  // personalization
  updatePersonalizationTemplateRedirect,
  // AI-originated actions
  setAiTemplateRedirects,
  setAiThemeRedirects,
} = redirectsSlice.actions;

export const redirectsReducer = redirectsSlice.reducer;
export const {
  selectedRedirectsState,
  selectedRedirects,
  selectedStoredRedirects,
  selectedRedirectErrors,
} = redirectsSlice.selectors;

export const getSliceSpecificRedirects = (
  redirectType: "template" | "theme" | "url"
) =>
  createSelector(selectedRedirects, (redirects) =>
    getSpecificRedirectsUtil(redirects, redirectType)
  );

export const getSliceVariationSpecificRedirects = (
  redirectType: "template" | "theme" | "url",
  variationId: string
) =>
  createSelector(selectedRedirects, (redirects) =>
    getSpecificVariationRedirectsUtil(redirects, redirectType, variationId)
  );

export const getRedirectsByVariationId = (variationId: string) =>
  createSelector(
    (state: RootState) => state.redirects.redirects,
    (redirects) =>
      redirects.filter((redirect) => redirect.variationId === variationId)
  );
